import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateIntegrationQuery, useGetExternalIntegrationQuery } from '../../../../queries';
import { useGetExternalIntegrationOAuthClientDisplay } from '../../../../queries/Integrations/useGetExternalIntegrationOAuthClientDisplay';
import { usePostExternalIntegrationOAuthClientMutation } from '../../../../queries/Integrations/usePostExternalIntegrationOAuthClient';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import { Button } from '@HoudiniUI/Button';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@HoudiniUI/Field';
import { Input } from '@HoudiniUI/Input';
import { IconTabTrigger, TabContent, TabList, TabRoot, TabTrigger } from '@HoudiniUI/Tabs';
import { THEME } from '@HoudiniUI/Theme/theme';
import filevineLogo from '../../../assets/logo_filevine.svg';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { SuccessCheckIcon } from '../../../components/svgs/icons/SuccessCheckIcon';
import { bs } from '../../../styles/styles';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum TabValues {
	Configure = 'configure',
	Manage = 'manage',
}

enum IntegrationState {
	NotConfigured = 'not-configured',
	Disconnected = 'disconnected',
	Connected = 'connected',
}

const secretPlaceholder = '***************';

// Filevine types the client id as a uuid, so a mis-pasted value is caught here rather than by a failed exchange
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function Filevine() {
	const [clientId, setClientId] = React.useState('');
	const [clientSecret, setClientSecret] = React.useState('');
	const [personalAccessToken, setPersonalAccessToken] = React.useState('');
	const [isEditing, setIsEditing] = React.useState(false);
	const history = useHistory();
	const userSession = useUserSession();
	const adminUserSession = userSession as AdminUserSessionContext;
	const params = useParams<{ tab: TabValues }>();
	const { logApiError } = useEventLogging('Filevine');
	const impersonationContext = userSession.impersonationContext;

	const integrationQuery = useGetExternalIntegrationQuery({
		impersonationContext,
		provider: Api.IntegrationProvider.Filevine,
	});

	const externalIntegrationOAuthClientDisplayQuery = useGetExternalIntegrationOAuthClientDisplay({
		enabled: !!integrationQuery.data?.configuration?.encryptedClientKeyId,
		impersonationContext,
		integrationProvider: Api.IntegrationProvider.Filevine,
	});

	const oauthClientMutation = usePostExternalIntegrationOAuthClientMutation({
		impersonationContext,
		onError: error => logApiError('saveFilevineCredentials', error),
		onSuccess: () => {
			// While impersonating, these pending actions are the admin's.
			if (!impersonationContext?.account?.id) {
				userSession.resolvePendingAction(Api.PendingActions.ConnectFilevine);
				// So later rejected credentials aren't hidden by an old dismissal.
				window.localStorage.removeItem(Api.PendingActions.ConnectFilevine);
			}
			invalidateIntegrationQuery({ impersonationContext, provider: Api.IntegrationProvider.Filevine });
			externalIntegrationOAuthClientDisplayQuery.refetch();
			setClientSecret('');
			setPersonalAccessToken('');
			setIsEditing(false);
			toast.success('Successfully connected to Filevine.');
		},
	});

	const changeTab = React.useCallback(
		(value: string) => {
			history.replace({
				...history.location,
				pathname: `/integrations/${Api.IntegrationProvider.Filevine}/${value}`,
			});
		},
		[history]
	);

	const integrationState = React.useMemo(() => {
		const configuration = integrationQuery.data?.configuration;

		if (!configuration?.encryptedClientKeyId) {
			return IntegrationState.NotConfigured;
		}

		return configuration.accountConnection?.isConnected ? IntegrationState.Connected : IntegrationState.Disconnected;
	}, [integrationQuery.data]);

	React.useEffect(() => {
		setClientId(externalIntegrationOAuthClientDisplayQuery.data?.clientId ?? '');
	}, [externalIntegrationOAuthClientDisplayQuery.data?.clientId]);

	const canConfigure = adminUserSession.isAdmin;
	const allowedTabs = canConfigure ? [TabValues.Configure, TabValues.Manage] : [TabValues.Manage];
	const defaultTab =
		canConfigure && integrationState !== IntegrationState.Connected ? TabValues.Configure : TabValues.Manage;
	const activeTab = allowedTabs.find(tab => tab === params.tab) ?? defaultTab;
	const urlHasValidTab = activeTab === params.tab;

	React.useEffect(() => {
		if (!integrationQuery.data || urlHasValidTab) {
			return;
		}

		changeTab(defaultTab);
	}, [changeTab, defaultTab, integrationQuery.data, urlHasValidTab]);

	const trimmedClientId = clientId.trim();
	const isClientIdValid = uuidPattern.test(trimmedClientId);
	// The API replaces the stored credentials as a whole, so every save needs all three
	const canSave = isClientIdValid && !!clientSecret.trim() && !!personalAccessToken.trim();

	const onSave = () => {
		if (!canSave) {
			return;
		}

		oauthClientMutation.mutate({
			integrationProvider: Api.IntegrationProvider.Filevine,
			clientId: trimmedClientId,
			clientSecret: clientSecret.trim(),
			personalAccessToken: personalAccessToken.trim(),
		});
	};

	const onEdit = () => {
		oauthClientMutation.reset();
		setIsEditing(true);
	};

	const onCancelEdit = () => {
		oauthClientMutation.reset();
		setIsEditing(false);
		setClientId(externalIntegrationOAuthClientDisplayQuery.data?.clientId ?? '');
		setClientSecret('');
		setPersonalAccessToken('');
	};

	if (integrationQuery.isPending || oauthClientMutation.isPending) {
		return <LoadingSpinner type='large' className={css(bs.absoluteCenter)} />;
	}

	// Showing the setup form here would let an admin replace credentials we could not confirm are absent.
	if (integrationQuery.isError) {
		return (
			<div className={css(styleSheet.container)}>
				<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
					We couldn&apos;t load your Filevine settings.
				</p>
				<Button onClick={() => integrationQuery.refetch()} variant='primary'>
					Try Again
				</Button>
			</div>
		);
	}

	const isNotConfigured = integrationState === IntegrationState.NotConfigured;
	const isDisconnected = integrationState === IntegrationState.Disconnected;
	const showForm = isNotConfigured || isDisconnected || isEditing;
	const saveErrorMessage = oauthClientMutation.error?.systemMessage;
	const showClientIdError = !!trimmedClientId && !isClientIdValid;

	return (
		<div className={css(styleSheet.container)}>
			<figure className={css(styleSheet.logo, integrationsStyleSheet.headerLogo)}>
				<img src={filevineLogo} alt='Filevine Logo' />
			</figure>
			<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
				Levitate can sync with Filevine, and pull client information, including names, email, phone number, birthdays,
				and more.
			</p>

			<TabRoot className={css(styleSheet.tabView)} value={activeTab} onValueChange={changeTab}>
				<TabList>
					{canConfigure ? (
						<IconTabTrigger
							value={TabValues.Configure}
							icon={
								<SuccessCheckIcon
									fillColor={isNotConfigured || isDisconnected ? THEME.colors.border.main : undefined}
									ringColor={isNotConfigured || isDisconnected ? THEME.colors.background.main : undefined}
								/>
							}
						>
							Configure
						</IconTabTrigger>
					) : null}

					<TabTrigger value={TabValues.Manage}>Manage</TabTrigger>
				</TabList>

				<TabContent value={TabValues.Configure}>
					<div className={css(styleSheet.section)}>
						{isNotConfigured ? (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								To get started, create API credentials for Levitate in Filevine and enter them below.
							</p>
						) : isDisconnected ? (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								Filevine is no longer accepting the saved credentials. Your client secret or personal access token may
								have expired. Generate a new one in Filevine and enter all three credentials below.
							</p>
						) : (
							<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
								Your Filevine account is configured to sync with Levitate.
							</p>
						)}

						{showForm ? (
							<div className={css(styleSheet.signInContainer)}>
								<ol className={css(integrationsStyleSheet.bodyText, styleSheet.setupSteps)}>
									<li>
										In Filevine, go to <b>Advanced &gt; Integrations</b> and activate Levitate.
									</li>
									<li>
										Go to <b>Advanced &gt; Service Accounts</b>, create a new service account and select Levitate.
									</li>
									<li>
										In <b>Account Manager &gt; Access Tokens</b>, copy the client ID and client secret from{' '}
										<b>Client Secrets</b>, then create a <b>Personal Access Token</b> for that service account.
									</li>
								</ol>
								<FieldGroup styles={[styleSheet.form]}>
									<Field data-invalid={showClientIdError}>
										<FieldLabel htmlFor='filevine-client-id'>Client ID</FieldLabel>
										<Input
											aria-invalid={showClientIdError}
											autoComplete='off'
											autoFocus={true}
											id='filevine-client-id'
											onChange={ev => setClientId(ev.target.value)}
											type='text'
											value={clientId}
										/>
										<FieldError
											errors={
												showClientIdError
													? [{ message: 'The client ID should look like 00000000-0000-0000-0000-000000000000.' }]
													: undefined
											}
										/>
									</Field>

									<Field>
										<FieldLabel htmlFor='filevine-client-secret'>Client Secret</FieldLabel>
										<Input
											autoComplete='new-password'
											id='filevine-client-secret'
											onChange={ev => setClientSecret(ev.target.value)}
											type='password'
											value={clientSecret}
										/>
									</Field>

									<Field>
										<FieldLabel htmlFor='filevine-personal-access-token'>Personal Access Token</FieldLabel>
										<Input
											autoComplete='new-password'
											id='filevine-personal-access-token'
											onChange={ev => setPersonalAccessToken(ev.target.value)}
											type='password'
											value={personalAccessToken}
										/>
										<FieldDescription>
											Create the token for the service account, not your own user. A token created for another user will
											sync with that user&apos;s access.
										</FieldDescription>
									</Field>

									<FieldError errors={saveErrorMessage ? [{ message: saveErrorMessage }] : undefined} />
								</FieldGroup>

								<div className={css(bs.horizontalStack)}>
									<Button onClick={onSave} variant='primary' disabled={!canSave}>
										Save
									</Button>

									{isEditing ? (
										<Button onClick={onCancelEdit} variant='outline'>
											Cancel
										</Button>
									) : null}
								</div>
							</div>
						) : (
							<div>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									<b>Client ID:</b> {clientId}
								</p>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									<b>Client Secret:</b> {secretPlaceholder}
								</p>
								<p className={css(integrationsStyleSheet.bodyText, integrationsStyleSheet.description)}>
									<b>Personal Access Token:</b> {secretPlaceholder}
								</p>

								<Button onClick={onEdit} variant='primary'>
									Edit
								</Button>
							</div>
						)}
					</div>
				</TabContent>

				<TabContent value={TabValues.Manage}>
					<div className={css(styleSheet.section)}>
						{integrationState === IntegrationState.Connected ? (
							<>
								<p className={css(integrationsStyleSheet.bodyText)}>
									Once you are configured, your Filevine data will show up in the contacts tab.
								</p>
								<Button asChild variant='primary'>
									<Link to='/people'>Contacts</Link>
								</Button>
							</>
						) : (
							<p className={css(integrationsStyleSheet.bodyText)}>
								{canConfigure
									? 'To sync with Filevine, enter your Filevine credentials in Configure.'
									: 'To sync with Filevine, please contact your administrator to configure your account.'}
							</p>
						)}
					</div>
				</TabContent>
			</TabRoot>
		</div>
	);
}
